// Enhanced data cache with compression, indexing, and memory management
const CACHE_KEY = 'digital_currencies_data_cache';
const CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours
const MAX_CACHE_SIZE = 50 * 1024 * 1024; // 50MB limit
const COMPRESSION_THRESHOLD = 1024; // Compress data larger than 1KB

// Indexed storage for faster lookups
class IndexedDataCache {
  constructor() {
    this.data = new Map();
    this.indexes = new Map();
    this.size = 0;
    this.lastAccess = new Map();
  }

  // Store data with automatic compression
  set(key, value) {
    const compressed = this.compressIfNeeded(value);
    const size = this.calculateSize(compressed);
    
    // Check if we need to evict old data
    if (this.size + size > MAX_CACHE_SIZE) {
      this.evictOldest();
    }
    
    this.data.set(key, compressed);
    this.size += size;
    this.lastAccess.set(key, Date.now());
    
    // Update indexes for faster lookups
    this.updateIndexes(key, value);
  }

  // Get data with automatic decompression
  get(key) {
    const compressed = this.data.get(key);
    if (!compressed) return null;
    
    console.log(`🔍 Debug: Cache get for key: ${key}, compressed data:`, compressed);
    
    // Update access time
    this.lastAccess.set(key, Date.now());
    
    const decompressed = this.decompressIfNeeded(compressed);
    console.log(`🔍 Debug: Cache get for key: ${key}, decompressed data:`, decompressed);
    console.log(`🔍 Debug: Cache get for key: ${key}, decompressed type:`, typeof decompressed);
    console.log(`🔍 Debug: Cache get for key: ${key}, decompressed is array:`, Array.isArray(decompressed));
    
    return decompressed;
  }

  // Check if key exists
  has(key) {
    return this.data.has(key);
  }

  // Remove specific key
  delete(key) {
    const compressed = this.data.get(key);
    if (compressed) {
      this.size -= this.calculateSize(compressed);
      this.data.delete(key);
      this.lastAccess.delete(key);
      this.removeFromIndexes(key);
    }
  }

  // Clear all data
  clear() {
    this.data.clear();
    this.indexes.clear();
    this.size = 0;
    this.lastAccess.clear();
  }

  // Get cache statistics
  getStats() {
    return {
      size: this.size,
      entries: this.data.size,
      maxSize: MAX_CACHE_SIZE,
      utilization: (this.size / MAX_CACHE_SIZE * 100).toFixed(2) + '%'
    };
  }

  // Compress data if it's large enough
  compressIfNeeded(data) {
    if (typeof data === 'string' && data.length > COMPRESSION_THRESHOLD) {
      try {
        // Simple compression for strings (remove whitespace, use shorter keys)
        return {
          compressed: true,
          data: data.replace(/\s+/g, ' ').trim()
        };
      } catch (error) {
        console.warn('Compression failed, storing uncompressed:', error);
        return { compressed: false, data };
      }
    }
    return { compressed: false, data };
  }

  // Decompress data if needed
  decompressIfNeeded(compressedData) {
    if (compressedData.compressed) {
      return compressedData.data;
    }
    return compressedData.data;
  }

  // Calculate approximate size of data
  calculateSize(data) {
    return JSON.stringify(data).length;
  }

  // Evict oldest accessed data
  evictOldest() {
    if (this.lastAccess.size === 0) return;
    
    let oldestKey = null;
    let oldestTime = Date.now();
    
    for (const [key, time] of this.lastAccess) {
      if (time < oldestTime) {
        oldestTime = time;
        oldestKey = key;
      }
    }
    
    if (oldestKey) {
      this.delete(oldestKey);
    }
  }

  // Update indexes for faster lookups
  updateIndexes(key, value) {
    // Index by date for quick date-based queries
    if (value.date) {
      if (!this.indexes.has('byDate')) {
        this.indexes.set('byDate', new Map());
      }
      const dateIndex = this.indexes.get('byDate');
      if (!dateIndex.has(value.date)) {
        dateIndex.set(value.date, []);
      }
      dateIndex.get(value.date).push(key);
    }
    
    // Index by location for quick location-based queries
    if (value.location_of_servers?.location_name) {
      if (!this.indexes.has('byLocation')) {
        this.indexes.set('byLocation', new Map());
      }
      const locationIndex = this.indexes.get('byLocation');
      const locationName = value.location_of_servers.location_name;
      if (!locationIndex.has(locationName)) {
        locationIndex.set(locationName, []);
      }
      locationIndex.get(locationName).push(key);
    }
  }

  // Remove from indexes
  removeFromIndexes(key) {
    for (const [indexName, index] of this.indexes) {
      for (const [indexKey, keys] of index) {
        const keyIndex = keys.indexOf(key);
        if (keyIndex > -1) {
          keys.splice(keyIndex, 1);
          if (keys.length === 0) {
            index.delete(indexKey);
          }
        }
      }
    }
  }

  // Query by date range
  queryByDateRange(startDate, endDate) {
    const dateIndex = this.indexes.get('byDate');
    if (!dateIndex) return [];
    
    const results = [];
    for (const [date, keys] of dateIndex) {
      const dateObj = new Date(date);
      if (dateObj >= startDate && dateObj <= endDate) {
        for (const key of keys) {
          const data = this.get(key);
          if (data) results.push(data);
        }
      }
    }
    return results;
  }

  // Query by location
  queryByLocation(locationName) {
    const locationIndex = this.indexes.get('byLocation');
    if (!locationIndex) return [];
    
    const keys = locationIndex.get(locationName) || [];
    return keys.map(key => this.get(key)).filter(Boolean);
  }
}

// Global cache instance
let globalCache = null;

// Initialize cache
const initializeCache = () => {
  if (!globalCache) {
    globalCache = new IndexedDataCache();
    
    // Try to restore from localStorage
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        console.log('🔍 Debug: Found cached data in localStorage');
        const parsed = JSON.parse(cached);
        console.log('🔍 Debug: Parsed cache data:', parsed);
        
        if (parsed.timestamp && Date.now() - parsed.timestamp < CACHE_EXPIRY) {
          console.log('🔍 Debug: Cache is still valid, restoring data...');
          // Restore cache data
          for (const [key, value] of Object.entries(parsed.data || {})) {
            console.log(`🔍 Debug: Restoring cache key: ${key}, value type:`, typeof value);
            globalCache.set(key, value);
          }
        } else {
          console.log('🔍 Debug: Cache expired, not restoring');
        }
      } else {
        console.log('🔍 Debug: No cached data found in localStorage');
      }
    } catch (error) {
      console.warn('🔍 Debug: Failed to restore cache from localStorage:', error);
    }
  }
  return globalCache;
};

// Public API functions
export const getCachedData = (key) => {
  const cache = initializeCache();
  return cache.get(key);
};

export const setCachedData = (key, data) => {
  const cache = initializeCache();
  cache.set(key, data);
  
  // Persist to localStorage periodically
  try {
    const cacheData = {
      timestamp: Date.now(),
      data: Object.fromEntries(cache.data)
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
  } catch (error) {
    console.warn('Failed to persist cache to localStorage:', error);
  }
};

export const hasCachedData = (key) => {
  const cache = initializeCache();
  return cache.has(key);
};

export const removeCachedData = (key) => {
  const cache = initializeCache();
  cache.delete(key);
};

export const clearCache = () => {
  if (globalCache) {
    globalCache.clear();
  }
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch (error) {
    console.warn('Failed to clear localStorage cache:', error);
  }
};

export const getCacheStats = () => {
  const cache = initializeCache();
  return cache.getStats();
};

export const queryCacheByDateRange = (startDate, endDate) => {
  const cache = initializeCache();
  return cache.queryByDateRange(startDate, endDate);
};

export const queryCacheByLocation = (locationName) => {
  const cache = initializeCache();
  return cache.queryByLocation(locationName);
};

// Cleanup function for memory management
export const cleanupCache = () => {
  if (globalCache) {
    // Remove old entries
    const now = Date.now();
    for (const [key, lastAccess] of globalCache.lastAccess) {
      if (now - lastAccess > CACHE_EXPIRY) {
        globalCache.delete(key);
      }
    }
  }
};

// Auto-cleanup every hour
setInterval(cleanupCache, 60 * 60 * 1000); 